import type { MouseEvent } from "react";

import type { Link } from "@/api";
import { cn } from "@/lib/utils";

/**
 * A Template's external links as small chips that open in a new tab. Stops
 * the click from reaching the surrounding card, so opening a link never
 * also starts an edit or a drag.
 */
export function LinkChips({
  links,
  className,
}: {
  links: Link[];
  className?: string;
}) {
  if (links.length === 0) return null;
  return (
    <span className={cn("flex flex-wrap gap-1", className)}>
      {links.map((l) => (
        <a
          key={l.url}
          href={l.url}
          target="_blank"
          rel="noopener noreferrer"
          title={l.url}
          onClick={(ev: MouseEvent) => ev.stopPropagation()}
          onDoubleClick={(ev: MouseEvent) => ev.stopPropagation()}
          className="inline-flex max-w-40 items-center gap-1 rounded-full border border-border bg-card px-2 py-0.5 text-[11px] text-foreground hover:bg-secondary"
        >
          <LinkIcon />
          <span className="truncate">{l.title}</span>
        </a>
      ))}
    </span>
  );
}

export function LinkIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="11"
      height="11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M6.5 9.5 9.5 6.5" />
      <path d="M7 4.5 8.2 3.3a2.6 2.6 0 0 1 3.7 3.7L10.7 8.2" />
      <path d="M9 11.5 7.8 12.7a2.6 2.6 0 0 1-3.7-3.7L5.3 7.8" />
    </svg>
  );
}
